import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Text,
  Caption1,
  Dropdown,
  Option,
  Button,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  DocumentTable20Regular,
  DocumentPdf20Regular,
  Document20Regular,
  Chat20Regular,
  Calendar20Regular,
  ArrowSortUp20Regular,
  ArrowSortDown20Regular,
  Edit20Regular,
  Delete20Regular,
  History20Regular,
  Eye20Regular,
  EyeOff20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Attach20Regular,
} from "@fluentui/react-icons";
import { StatusItemDto } from "../../../services/status-items.service";
import { ExcelService } from "../../../services/excel.service";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { getStatusItemColor, getStatusItemTextColor, normalizeStatusItemStatus } from "../../../utils/statusItemHelpers";
import { UserRole } from "../../../types/UserRole";
import {
  getAvailableStatusesForRole,
  canUserDeleteStatusItem,
} from "../../../utils/statusItemTransitions";
import DocumentVersionHistoryDialog from "./DocumentVersionHistoryDialog";
import DocumentPickerDialog from "./DocumentPickerDialog";
import { AccountBalanceDetails } from "./AccountBalanceDetails";
import { WorkpaperBalanceDto } from "../../../services/xero-integration.service";
import { formatCurrency } from "../../../utils/formatters";
import { useTypographyStyles } from "../../../styles/typography.styles";

/**
 * Get the appropriate icon component based on status item type
 */
const getItemTypeIcon = (type?: string) => {
  switch (type) {
    case "document":
      return DocumentPdf20Regular;
    case "workflow_item":
      return DocumentTable20Regular;
    default:
      return Document20Regular;
  }
};

interface StatusItemTableProps {
  items: StatusItemDto[];
  workpaperBalances: Map<string, WorkpaperBalanceDto>;
  sortConfig: {
    key: string;
    direction: "asc" | "desc";
  };
  onSort: (key: string) => void;
  userRole: UserRole;
  onStatusChange: (itemId: number, newStatus: string) => void;
  onNameChange: (itemId: number, newName: string) => void;
  onDelete: (itemId: number) => void;
  onDiscussionClick: (item: StatusItemDto) => void;
  onToggleVisibility: (itemId: number, isHidden: boolean) => void;
  onDocumentChange: (itemId: number, documentId: string | null) => void;
  clientId?: string;
  onRefresh?: () => void;
}

const useStyles = makeStyles({
  tableContainer: {
    // Claims remaining vertical space inside the page's flex column and scrolls
    // internally — keeps the page header / stats / filters pinned in view even
    // when the item list is long.
    flex: "1 1 0",
    minHeight: 0,
    overflow: "auto",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  thead: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  th: {
    padding: tokens.spacingVerticalM,
    textAlign: "left",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  thSortable: {
    cursor: "pointer",
    userSelect: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  thContent: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  tbody: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tr: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  trHidden: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: "background-color 0.2s ease",
    backgroundColor: tokens.colorNeutralBackground3,
    opacity: 0.7,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground4,
      opacity: 0.85,
    },
  },
  td: {
    padding: tokens.spacingVerticalM,
    verticalAlign: "top",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  nameIcon: {
    color: tokens.colorNeutralForeground2,
  },
  editButton: {
    minWidth: "auto",
  },
  nameInput: {
    flexGrow: 1,
    minWidth: 0,
  },
  statusBadge: {
    fontWeight: tokens.fontWeightSemibold,
  },
  discussionCount: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    cursor: "pointer",
    padding: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusSmall,
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  discussionIcon: {
    color: tokens.colorNeutralForeground2,
  },
  dateCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  calendarIcon: {
    color: tokens.colorNeutralForeground2,
  },
  statusDropdown: {
    minWidth: "120px",
    maxWidth: "160px",
    "& button": {
      minHeight: "24px",
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      fontSize: tokens.fontSizeBase200,
      fontWeight: tokens.fontWeightSemibold,
      borderRadius: tokens.borderRadiusMedium,
      color: "#FFFFFF !important",
    },
    "& button span": {
      color: "#FFFFFF !important",
    },
  },
  balanceCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  balanceCellClickable: {
    cursor: "pointer",
    borderRadius: tokens.borderRadiusSmall,
    padding: tokens.spacingHorizontalXS,
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  expandIcon: {
    color: tokens.colorNeutralForeground2,
  },
  expandedRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  expandedCell: {
    padding: 0,
  },
  sheetLink: {
    marginLeft: "28px",
    cursor: "pointer",
    color: tokens.colorBrandForeground1,
    ":hover": {
      textDecoration: "underline",
    },
  },
  documentNameLink: {
    cursor: "pointer",
    color: tokens.colorBrandForeground1,
    ":hover": {
      textDecoration: "underline",
    },
  },
});

/**
 * Editable table of status items — inline status / name / document / visibility
 * controls, role-aware permissions, sortable columns, and Xero balance details
 * shown alongside each linked workpaper.
 */
export const StatusItemTable: React.FC<StatusItemTableProps> = ({
  items,
  workpaperBalances,
  sortConfig,
  onSort,
  userRole,
  onStatusChange,
  onNameChange,
  onDelete,
  onDiscussionClick,
  onToggleVisibility,
  onDocumentChange,
  clientId,
  onRefresh,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();
  const { getWorksheetName, isWorkbookSignedOff, isStandalone } = useWorkflowIntelligence();
  const availableStatuses = getAvailableStatusesForRole(userRole);
  const canDelete = canUserDeleteStatusItem(userRole);
  // Partners can move an item off "Partner Reviewed" up until the workbook is
  // signed off. Once partner-signed, that status is terminal for everyone.
  const canEditTerminalStatus = userRole === UserRole.Partner && !isWorkbookSignedOff;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StatusItemDto | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [itemForVersionHistory, setItemForVersionHistory] = useState<StatusItemDto | null>(null);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [itemForDocumentPicker, setItemForDocumentPicker] = useState<StatusItemDto | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const toggleRowExpansion = (itemId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (editingItemId !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItemId]);

  const handleEditStart = (item: StatusItemDto) => {
    setEditingItemId(item.id);
    setEditName(item.name);
  };

  const handleEditSave = () => {
    if (editingItemId !== null && editName.trim() && editName.trim() !== items.find(i => i.id === editingItemId)?.name) {
      onNameChange(editingItemId, editName.trim());
    }
    setEditingItemId(null);
    setEditName("");
  };

  const handleEditCancel = () => {
    setEditingItemId(null);
    setEditName("");
  };

  const handleDeleteClick = (item: StatusItemDto) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      onDelete(itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleVersionHistoryClick = (item: StatusItemDto) => {
    setItemForVersionHistory(item);
    setVersionHistoryOpen(true);
  };

  const handleVersionHistoryClose = () => {
    setVersionHistoryOpen(false);
    setItemForVersionHistory(null);
  };

  const handleDocumentPickerClick = (item: StatusItemDto) => {
    setItemForDocumentPicker(item);
    setDocumentPickerOpen(true);
  };

  const handleDocumentPickerClose = () => {
    setDocumentPickerOpen(false);
    setItemForDocumentPicker(null);
  };

  const handleDocumentSelect = (documentId: string | null) => {
    if (itemForDocumentPicker) {
      onDocumentChange(itemForDocumentPicker.id, documentId);
    }
  };

  const handleSheetClick = async (sheetId: string) => {
    // No Excel host = no sheet to jump to. Leave the row clickable but
    // no-op; a future enhancement could hide the affordance entirely.
    if (isStandalone) return;
    try {
      await ExcelService.selectCellById(sheetId, "A1");
    } catch (error) {
      console.error("Failed to navigate to sheet:", error);
    }
  };

  const renderSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowSortUp20Regular />
    ) : (
      <ArrowSortDown20Regular />
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
  };

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th
              className={`${styles.th} ${styles.thSortable}`}
              onClick={() => onSort("name")}
            >
              <div className={styles.thContent}>
                <Text>Name</Text>
                {renderSortIcon("name")}
              </div>
            </th>
            <th
              className={`${styles.th} ${styles.thSortable}`}
              onClick={() => onSort("status")}
            >
              <div className={styles.thContent}>
                <Text>Status</Text>
                {renderSortIcon("status")}
              </div>
            </th>
            <th
              className={`${styles.th} ${styles.thSortable}`}
              onClick={() => onSort("discussionCount")}
            >
              <div className={styles.thContent}>
                <Text>Discussion Count</Text>
                {renderSortIcon("discussionCount")}
              </div>
            </th>
            <th className={styles.th}>
              <Text>Xero Balance</Text>
            </th>
            <th
              className={`${styles.th} ${styles.thSortable}`}
              onClick={() => onSort("lastUpdated")}
              hidden
            >
              <div className={styles.thContent}>
                <Text>Last Updated</Text>
                {renderSortIcon("lastUpdated")}
              </div>
            </th>
            <th className={styles.th}>
              <Text>Actions</Text>
            </th>
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {items.map((item) => {
            const statusColor = getStatusItemColor(item.status);
            const statusTextColor = getStatusItemTextColor(item.status);
            const ItemIcon = getItemTypeIcon(item.type);

            return (
              <React.Fragment key={item.id}>
              <tr
                className={item.isHidden ? styles.trHidden : styles.tr}
                onMouseEnter={() => setHoveredRowId(item.id)}
                onMouseLeave={() => setHoveredRowId(null)}
              >
                {/* Name Column */}
                <td className={styles.td}>
                  <div className={styles.nameCell}>
                    <ItemIcon className={styles.nameIcon} />
                    {editingItemId === item.id ? (
                      <Input
                        ref={editInputRef}
                        className={styles.nameInput}
                        size="small"
                        value={editName}
                        onChange={(_e, data) => setEditName(data.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleEditSave();
                          } else if (e.key === "Escape") {
                            handleEditCancel();
                          }
                        }}
                        onBlur={handleEditSave}
                      />
                    ) : (
                      <>
                        {item.type === "document" ? (
                          <Text
                            className={`${typography.formLabel} ${styles.documentNameLink}`}
                            role="button"
                            tabIndex={0}
                            title="Open in Documents"
                            onClick={() =>
                              navigate(`/documents?search=${encodeURIComponent(item.name)}`)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/documents?search=${encodeURIComponent(item.name)}`);
                              }
                            }}
                          >
                            {item.name}
                          </Text>
                        ) : (
                          <Text className={typography.formLabel}>{item.name}</Text>
                        )}
                        {/* Always rendered (visibility-hidden when not hovered) so the
                            cell keeps its width — toggling render causes the table to
                            reflow and flicker as the user moves their mouse. */}
                        <Button
                          className={styles.editButton}
                          appearance="subtle"
                          size="small"
                          icon={<Edit20Regular />}
                          onClick={() => handleEditStart(item)}
                          aria-label={`Rename ${item.name}`}
                          title="Rename"
                          style={{ visibility: hoveredRowId === item.id ? "visible" : "hidden" }}
                        />
                      </>
                    )}
                  </div>
                  {item.type === "document" && item.latestDocumentVersion ? (
                    <Caption1 style={{ marginLeft: "28px" }}>
                      Version: {item.latestDocumentVersion}
                    </Caption1>
                  ) : item.sheetId ? (
                    (() => {
                      const sheetName = getWorksheetName(item.sheetId);
                      if (!sheetName) {
                        // In standalone there's no Excel connection, so every
                        // sheet name lookup fails — showing "[Missing sheet]"
                        // on every row scares users. Hide the caption entirely.
                        // In Excel a missing name is genuinely anomalous
                        // (sheet deleted / renamed) and worth surfacing.
                        if (isStandalone) return null;
                        return (
                          <Caption1 style={{ marginLeft: "28px", fontStyle: "italic", color: "gray" }}>
                            [Missing sheet]
                          </Caption1>
                        );
                      }
                      return (
                        <Caption1
                          className={styles.sheetLink}
                          onClick={() => handleSheetClick(item.sheetId!)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleSheetClick(item.sheetId!);
                            }
                          }}
                        >
                          {sheetName}
                        </Caption1>
                      );
                    })()
                  ) : null}
                </td>

                {/* Status Column */}
                <td className={styles.td}>
                  {/* Partner Reviewed is editable only by Partners while the
                      workbook isn't signed off; everyone else (and everyone
                      after sign-off) sees the locked badge. */}
                  {availableStatuses.length > 0 &&
                  (normalizeStatusItemStatus(item.status) !== "Partner Reviewed" || canEditTerminalStatus) ? (
                    <Dropdown
                      className={styles.statusDropdown}
                      value={item.status || "Open"}
                      onOptionSelect={(_e, data) => {
                        if (data.optionValue) {
                          onStatusChange(item.id, data.optionValue as string);
                        }
                      }}
                      style={{ backgroundColor: statusColor, color: "#FFFFFF" }}
                    >
                      {availableStatuses.map((status) => (
                        <Option key={status} value={status}>
                          {status}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    <Badge
                      appearance="filled"
                      style={{ backgroundColor: statusColor, color: statusTextColor }}
                      className={styles.statusBadge}
                    >
                      {item.status || "Open"}
                    </Badge>
                  )}
                </td>

                {/* Discussion Count Column */}
                <td className={styles.td}>
                  <div
                    className={styles.discussionCount}
                    onClick={() => onDiscussionClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onDiscussionClick(item);
                      }
                    }}
                  >
                    <Chat20Regular className={styles.discussionIcon} />
                    <Text>{item.discussionCount}</Text>
                  </div>
                </td>

                {/* Xero Balance Column */}
                <td className={styles.td}>
                  {(() => {
                    const balance = workpaperBalances.get(item.name);
                    if (!balance) {
                      return <Text className={typography.bodySmall}>-</Text>;
                    }

                    const isExpanded = expandedRows.has(item.id);
                    const hasAccounts = balance.accounts.length > 0;

                    if (hasAccounts) {
                      return (
                        <div
                          className={`${styles.balanceCell} ${styles.balanceCellClickable}`}
                          onClick={() => toggleRowExpansion(item.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              toggleRowExpansion(item.id);
                            }
                          }}
                        >
                          {isExpanded ? (
                            <ChevronDown20Regular className={styles.expandIcon} />
                          ) : (
                            <ChevronRight20Regular className={styles.expandIcon} />
                          )}
                          <Text className={typography.bodySmallStrong}>
                            {formatCurrency(balance.balance)}
                          </Text>
                        </div>
                      );
                    }

                    return (
                      <div className={styles.balanceCell}>
                        <Text className={typography.bodySmallStrong}>
                          {formatCurrency(balance.balance)}
                        </Text>
                      </div>
                    );
                  })()}
                </td>

                {/* Last Updated Column */}
                <td className={styles.td} hidden>
                  <div className={styles.dateCell}>
                    <Calendar20Regular className={styles.calendarIcon} />
                    <Text>{formatDate(item.lastUpdated)}</Text>
                  </div>
                </td>

                {/* Actions Column */}
                <td className={styles.td}>
                  <Button
                    appearance="subtle"
                    icon={item.isHidden ? <Eye20Regular /> : <EyeOff20Regular />}
                    onClick={() => onToggleVisibility(item.id, !item.isHidden)}
                    aria-label={item.isHidden ? `Show ${item.name}` : `Hide ${item.name}`}
                    title={item.isHidden ? "Show item" : "Hide item"}
                  />
                  {item.type === "document" && clientId && (
                    <Button
                      appearance="subtle"
                      icon={<Attach20Regular />}
                      onClick={() => handleDocumentPickerClick(item)}
                      aria-label={`Link document for ${item.name}`}
                      title={item.documentId ? "Change linked document" : "Link document"}
                    />
                  )}
                  {item.type === "document" && item.documentId && (
                    <Button
                      appearance="subtle"
                      icon={<History20Regular />}
                      onClick={() => handleVersionHistoryClick(item)}
                      aria-label={`View version history for ${item.name}`}
                      title="Version History"
                    />
                  )}
                  {canDelete && (
                    <Button
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      onClick={() => handleDeleteClick(item)}
                      aria-label={`Delete ${item.name}`}
                    />
                  )}
                </td>
              </tr>
              {/* Expanded Account Details Row */}
              {expandedRows.has(item.id) && workpaperBalances.get(item.name) && (
                <tr className={styles.expandedRow}>
                  <td colSpan={7} className={styles.expandedCell}>
                    <AccountBalanceDetails
                      accounts={workpaperBalances.get(item.name)!.accounts}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => data.open || handleDeleteCancel()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Status Item</DialogTitle>
            <DialogContent>
              Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Document Version History Dialog */}
      <DocumentVersionHistoryDialog
        isOpen={versionHistoryOpen}
        onDismiss={handleVersionHistoryClose}
        statusItem={itemForVersionHistory}
        onVersionUploaded={onRefresh}
      />

      {/* Document Picker Dialog */}
      {clientId && (
        <DocumentPickerDialog
          isOpen={documentPickerOpen}
          onDismiss={handleDocumentPickerClose}
          clientId={clientId}
          currentDocumentId={itemForDocumentPicker?.documentId}
          onSelect={handleDocumentSelect}
        />
      )}
    </div>
  );
};
